import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, CreditCard, GraduationCap, Save, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { apiUrl } from '@/config/api';

// Interfaces for state
interface Schedule {
  id: string;
  name: string;
  academicYear: number;
}
interface Structure {
  id: string;
  nameFormal: string;
  nameShort: string;
}
interface Fee {
  id: string;
  name: string;
  amount: number;
  type: string;
}

const API_BASE = apiUrl('/api/offerings');

const offeringFormSchema = z.object({
  scheduleId: z.string().uuid('必須項目です'),
  structureId: z.string().uuid('必須項目です'),
  capacity: z.coerce.number().int().min(1, '定員は1名以上である必要があります'),
  feeProductId: z.string().uuid().nullable().optional(),
  interviewRequired: z.boolean(),
});

type OfferingFormValues = z.infer<typeof offeringFormSchema>;

export default function ExamOfferingForm() {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [structures, setStructures] = useState<Structure[]>([]);
  const [fees, setFees] = useState<Fee[]>([]);

  const form = useForm<OfferingFormValues>({
    resolver: zodResolver(offeringFormSchema),
    defaultValues: {
      scheduleId: '',
      structureId: '',
      capacity: 0,
      feeProductId: null,
      interviewRequired: false,
    },
  });

  const fetchMasterData = useCallback(async () => {
    try {
      // In a real app, these would be separate stable APIs
      const [sRes, stRes, pRes] = await Promise.all([
        fetch(apiUrl('/api/exams')),
        fetch(apiUrl('/api/organizations')), // This returns a tree normally
        fetch(apiUrl('/api/fees')),
      ]);

      if (sRes.ok) setSchedules((await sRes.json()) as Schedule[]);
      if (pRes.ok) setFees((await pRes.json()) as Fee[]);

      // For structures, we might need a flattened list.
      // Mocking a simple list for now or assuming the API handles basic info
      if (stRes.ok) {
        const orgData = (await stRes.json()) as Structure[];
        // Simple flatten if it's a tree (recursive helper would be better)
        setStructures(orgData);
      }
    } catch (error) {
      console.error('Failed to fetch master data', error);
    }
  }, []);

  const fetchOffering = useCallback(
    async (offeringId: string) => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/${offeringId}`);
        if (res.ok) {
          const data = (await res.json()) as OfferingFormValues;
          form.reset({
            scheduleId: data.scheduleId,
            structureId: data.structureId,
            capacity: data.capacity,
            feeProductId: data.feeProductId,
            interviewRequired: !!data.interviewRequired,
          });
        }
      } catch (error) {
        console.error('Failed to fetch offering', error);
      } finally {
        setLoading(false);
      }
    },
    [form],
  );

  useEffect(() => {
    fetchMasterData();
    if (isEdit) {
      fetchOffering(id);
    }
  }, [id, isEdit, fetchMasterData, fetchOffering]);

  const onSubmit = async (values: OfferingFormValues) => {
    try {
      setLoading(true);
      const url = isEdit ? `${API_BASE}/${id}` : API_BASE;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        navigate('/admin/master/offerings');
      }
    } catch (error) {
      console.error('Failed to save offering', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/master/offerings')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {isEdit ? '募集枠の編集' : '新規募集枠の登録'}
          </h1>
          <p className="text-gray-500">入試日程と募集単位（学科・コース等）を紐付け、定員を設定します。</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    募集定義
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="scheduleId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>対象入試日程</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="入試日程を選択してください" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {schedules.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} ({s.academicYear}年度)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="structureId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>募集組織（学部・学科・コース）</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="所属組織を選択してください" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {structures.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.nameFormal || s.nameShort}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>募集の最小単位を選択します。</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                    料金設定
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="feeProductId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>適用する検定料マスタ</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="料金項目を選択（任意）" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="null">設定なし（無料）</SelectItem>
                            {fees
                              .filter((p) => p.type === 'EXAM_FEE')
                              .map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} (¥{p.amount?.toLocaleString()})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>定員・選考内容</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="capacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>募集人員（定員）</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="interviewRequired"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel>面接試験あり</FormLabel>
                          <FormDescription>面接評価が必要な場合に有効にします。</FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <div className="flex flex-col gap-3">
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" /> 保存
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  className="w-full"
                  onClick={() => navigate('/admin/master/offerings')}
                >
                  キャンセル
                </Button>
                {isEdit && (
                  <Button
                    variant="ghost"
                    type="button"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> この募集枠を解除
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
